import type { TurnCost } from './types';
import { fmtMs, fmtUsd, type TimingBreakdown } from './turnTiming';

type Props = {
  timing?: TimingBreakdown;
  cost?: TurnCost;
  /** Running conversation cost total (USD). */
  totalCost?: number;
};

/**
 * Dev-only per-turn timing + cost badge shown under an assistant message.
 * Rendered ONLY in dev builds AND only when the server attached cost (it never
 * does in production, so this is doubly gated) — see CONCEPT §4/§8.
 */
const TurnDebugBadge = ({ timing, cost, totalCost }: Props) => {
  if (!import.meta.env.DEV || !cost) return null;

  const parts: string[] = [];
  if (timing) {
    if (timing.spans.reasoning) parts.push(`Reasoning ${fmtMs(timing.spans.reasoning)}`);
    if (timing.spans.web_search) parts.push(`Web search ${fmtMs(timing.spans.web_search)}`);
    if (timing.spans.writing) parts.push(`Responding ${fmtMs(timing.spans.writing)}`);
    if (timing.firstToken != null) parts.push(`First token ${fmtMs(timing.firstToken)}`);
  }
  parts.push(`In ${cost.input_tokens}tok ${fmtUsd(cost.input_cost)}`);
  parts.push(`Out ${cost.output_tokens}tok ${fmtUsd(cost.output_cost)}`);
  parts.push(`Web ${cost.web_search_calls}× ${fmtUsd(cost.web_search_cost)}`);

  const totals: string[] = [];
  if (timing) totals.push(`Total ${fmtMs(timing.total)}`);
  totals.push(`Cost ${fmtUsd(cost.total_cost)}`);
  if (totalCost != null && totalCost > 0) totals.push(`Convo ${fmtUsd(totalCost)}`);

  return (
    <div className="yst-ml-10 yst-mt-1 yst-flex yst-flex-wrap yst-gap-x-2 yst-gap-y-0.5 yst-text-[10px] yst-text-slate-400">
      {parts.map((p, i) => (
        <span key={`p-${i}`}>{p}</span>
      ))}
      <span className="yst-font-medium yst-text-slate-500">{totals.join(' · ')}</span>
    </div>
  );
};

export default TurnDebugBadge;
